import React from 'react';
import PropTypes from 'prop-types';
import CardListContainer from 'containers/CardListContainer/CardListContainer';
import FontIcon from 'components/FontIcon';
import ExpandableList from 'components/ExpandableList/ExpandableList';
import Modal from 'react-modal';
import { filter, isEmpty } from 'lodash';
import Button from 'components/Button';
import ProductFilterA from 'components/ProductFilter/ProductFilterA';
import { buildProductListStructuredData } from 'utils/StructuredData';
import classes from './ProductSearchA.module.scss';

const ProductSearchA = (props) => {
  const {
    selectedOptions = [],
    pendingOptions = [],
    functionOptions: {
      onSearchInput,
      onFilterItems,
      onShowAllItems,
      onClearFilters,
      onShowFilters,
      onCancelFilters,
      onApplyFilters,
    },
    filterDisplays: {
      desktop = [],
      mobile = [],
    },
    labelOptions: {
      mainTitle,
      filterTitle,
      displayConfiguration,
      template,
    },
    searchInput,
    itemsShown,
    showFilters,
    items = [],
    originalItems = [],
  } = props;

  // Create each checkbox filter category group
  function createFilter(filterObj, options) {
    const {
      title,
      category,
      filters = {},
      filterFunction,
    } = filterObj;

    const body = (
      <div key={`ProductFilterA-${title}`} className={classes.filterContainer}>
        <ProductFilterA
          title={title}
          category={category}
          items={filters}
          selectedItems={filter(options, (f) => f.type === category).map((i) => i.value)}
          onFilterItems={filterFunction}
        />
      </div>
    );
    return { title, body };
  }

  const noItems = items.length === 0
    && pendingOptions.length === 0
    && selectedOptions.length === 0
    && searchInput === '';

  const getAppliedFilterLabelOnMobile = (type, value) => {
    const { filterOptions } = props;
    const filterType = type.split('selected') || [];
    if (filterType.length > 1) {
      return `${value} (${filterOptions[`filter${filterType[1]}`][value] || 0})`;
    }
    return value;
  };

  const structuredData = buildProductListStructuredData(originalItems);

  const selectedValues = [searchInput, ...selectedOptions.map((i) => i.value)].filter(Boolean).join(', ');

  const desktopFilters = desktop.filter((i) => Object.keys(i.filters).length > 1);
  const mobileFilters = mobile.filter((i) => Object.keys(i.filters).length > 1);

  return (
    <div className={classes.container}>
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: structuredData }}
      />
      {mainTitle &&
      <h2 className={classes.title}>
        {mainTitle}
      </h2>}
      {noItems &&
        <div className={classes.noItems}>No {filterTitle || 'items'} found.</div>}
      {!noItems &&
      <div className={classes.wrapper}>
        {!isEmpty(mobileFilters) &&
          <Modal
            isOpen={showFilters}
            onRequestClose={onShowFilters}
            shouldCloseOnOverlayClick
            ariaHideApp={false}
            className={classes.modal}
            overlayClassName={classes.overlayModal}
            closeTimeoutMS={250}
          >
            <div className={classes.filterTitleContainer}>
              <div className={classes.filterTitle}>Filter By:</div>
              <button
                aria-label='Close'
                className={classes.filterTitleClose}
                onClick={() => onCancelFilters()}
                type='button'
              >
                <FontIcon icon='far fa-times' className={classes.filterTitleIcon} />
              </button>
            </div>
            <ExpandableList
              type='ExpandableListItemC'
              items={mobileFilters.map((i) => createFilter(i, pendingOptions))}
              renderStructuredData={false}
            />
            <div className={classes.modalButtonContainer}>
              <div className={classes.modalButtonClearContainer}>
                <Button subType='ButtonC' title='Clear' onClick={() => onClearFilters()} />
              </div>
              <div className={classes.modalButtonApplyContainer}>
                <Button subType='ButtonC' title='Select' onClick={() => onApplyFilters()} />
              </div>
            </div>
          </Modal>}
        {!isEmpty(desktopFilters) &&
          <div className={classes.left}>
            <div className={classes.desktopFilters}>
              {desktopFilters.length > 0 &&
                <div key='ProductFilterA-filters-section' className={classes.sectionTitle}>Filter Results</div>}
              {desktopFilters.map((i) => createFilter(i, selectedOptions)?.body)}
            </div>
          </div>}
        <div className={classes.right}>
          <div className={classes.top}>
            <div className={classes.searchContainer}>
              <input
                className={classes.searchInput}
                type='search'
                value={searchInput}
                placeholder='Search'
                onChange={onSearchInput}
                aria-label='Search'
              />
              <FontIcon icon='far fa-search' className={classes.searchIcon} />
            </div>
            {(searchInput || selectedOptions.length > 0) &&
              <div
                className={classes.hidden}
                aria-live='polite'
                aria-atomic='true'
                aria-relevant='additions'
              >
                {`You searched for ${selectedValues} and found ${items.length} ${filterTitle}`}
              </div>}
            <div className={classes.summaryContainer}>
              <div className={classes.summaryTitle}>
                Showing {itemsShown < items.length ? `${itemsShown} of` : 'all'} {items.length} {filterTitle}
              </div>
              <div className={classes.summaryButtons}>
                <button
                  aria-label='Show All'
                  className={classes.showAllItems}
                  onClick={() => onShowAllItems()}
                  type='button'
                >
                  Show All
                </button>
                <button
                  aria-label='Show Filters'
                  className={classes.showFilters}
                  onClick={() => onShowFilters()}
                  type='button'
                >
                  Filters ({selectedOptions.length})
                </button>
                <button
                  aria-label='Clear Filters'
                  className={classes.clearFilters}
                  onClick={() => onClearFilters()}
                  type='button'
                >
                  Clear Filters
                </button>
                {/*
                  We are adding this ghost icon because the fas font was not being loaded until you select a filter
                  the first time. Thus you would get a delay while displaying the checkbox. There was other solutions
                  like adding this directly on the ProductFilterA component, howeveer, you would need to manipulate
                  the expandable list for mobile too if adding it at the top level (title), or you would end with
                  multiple hidden icons if you decided to put it besides each filter. You could also try using a css
                  class on any other element and manipulate the css of that class, but that approach tries to load
                  fonts from a wrong path.
                  So, even though this might look hacky, it is a one place solution and with no 404 fonts.
                */}
                <FontIcon icon='fas fa-check-square' className={classes.hiddenIcon} />
              </div>
              {selectedOptions.length > 0 &&
                <div className={classes.summaryFilters}>
                  <div className={classes.summaryFiltersTitle}>Selected Filters:</div>
                  {selectedOptions.map((i, idx) => (
                    <button
                      key={idx}
                      className={classes.filterItemContainer}
                      onClick={() => onFilterItems(i.type, i.value)}
                      aria-label={i.value}
                      type='button'
                    >
                      <div
                        className={classes.filterItem}
                        dangerouslySetInnerHTML={{ __html: getAppliedFilterLabelOnMobile(i.type, i.value) }}
                      />
                      <FontIcon icon='far fa-times' className={classes.filterIcon} />
                    </button>
                  ))}
                </div>}
            </div>
          </div>
          {items.length === 0 &&
          <div className={classes.noItems}>No {filterTitle || 'items'} found with the selected filters.</div>}
          {items.length > 0 &&
          <div className={classes.bottom}>
            <CardListContainer
              items={items}
              divided
              withSearch={false}
              itemIncrement={itemsShown}
              displayOptions={{ template, displayConfiguration }}
            />
          </div>}
        </div>
      </div>}
    </div>
  );
};

ProductSearchA.propTypes = {
  selectedOptions: PropTypes.array,
  pendingOptions: PropTypes.array,
  filterDisplays: PropTypes.object,
  functionOptions: PropTypes.object,
  labelOptions: PropTypes.object,
  showFilters: PropTypes.bool,
  searchInput: PropTypes.string,
  items: PropTypes.array,
  itemsShown: PropTypes.number,
  filterOptions: PropTypes.object,
  originalItems: PropTypes.array,
};

export default ProductSearchA;
